import { Chip, Tooltip, Typography } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { useTranslation } from 'react-i18next'
import { PiCheckThin, PiInfoLight } from 'react-icons/pi'
import { VscClose } from 'react-icons/vsc'
import { useDarkMode } from '../contexts/DarkModeContext'
import { AgamiExperience } from '../types/supabase'
import { AgamiUploadFormData } from './UploadAgamiInterface'

interface AgamiExperienceSelectorProps {
  userExperiences: AgamiExperience[]
  formData: AgamiUploadFormData
  setFormData: (data: AgamiUploadFormData | ((prev: AgamiUploadFormData) => AgamiUploadFormData)) => void
}

const AgamiExperienceSelector: React.FC<AgamiExperienceSelectorProps> = ({
  userExperiences,
  formData,
  setFormData,
}) => {
  const { darkMode } = useDarkMode()
  const { t } = useTranslation()

  // toggle experience
  const handleExpChipClick = (exp: AgamiExperience) => {
    setFormData((prevData) =>
      prevData.experiences.includes(exp.id)
        ? {
            ...prevData,
            experiences: prevData.experiences.filter((prevExp) => prevExp !== exp.id),
          }
        : {
            ...prevData,
            experiences: [...prevData.experiences, exp.id],
          }
    )
  }

  return (
    <div className="flex flex-col gap-2 items-star bg-white dark:bg-primary rounded-lg p-4 shadow-sm">
      <div className="flex gap-1 justify-start items-center mb-2">
        <Typography
          className="font-bold text-primary text-lg font-barlow uppercase"
          data-testid="add-agami-to-experience-title"
        >
          {t('upload.addToExperience')}
        </Typography>
        <Tooltip
          content={
            <div className="w-[300px]">
              <Typography variant="small" className="font-bold">
                {t('upload.addToExperienceDescription')}
              </Typography>
            </div>
          }
          placement="right"
          interactive
        >
          <span>
            <PiInfoLight className="text-primary dark:text-base-gray-400" />
          </span>
        </Tooltip>
      </div>

      <div className="flex gap-2 flex-wrap">
        {userExperiences?.map((exp) => {
          const isSelected = formData.experiences.includes(exp.id)
          let chipVariant: 'filled' | 'outlined'
          let chipColor: string

          if (darkMode) {
            chipVariant = 'filled'
            chipColor = isSelected ? 'blue-gray' : 'primary'
          } else {
            chipVariant = isSelected ? 'filled' : 'outlined'
            chipColor = isSelected ? 'primary' : 'gray'
          }

          const chipIcon = formData.experiences.includes(exp.id) ? (
            <PiCheckThin className="h-5 w-5" />
          ) : (
            <VscClose className="h-5 w-5" />
          )

          return (
            <span
              key={exp.id}
              className="inline-block cursor-pointer"
              onClick={() => handleExpChipClick(exp)}
            >
              <Chip
                className="inline-block"
                data-testclass="add-agami-to-experience-exp-chip"
                value={exp.name}
                variant={chipVariant}
                color={chipColor as colors}
                icon={chipIcon}
              />
            </span>
          )
        })}
      </div>
    </div>
  )
}

export default AgamiExperienceSelector
